import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Compass } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/resources", label: "Resources" },
  { to: "/products", label: "Products" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-brand-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-sage text-white shadow-soft group-hover:bg-brand-sageDark transition-colors">
            <Compass className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span className="font-display text-lg font-semibold text-brand-ink leading-none">
            One Step Ahead
            <span className="block text-[10px] uppercase tracking-[0.2em] text-brand-inkMuted font-medium mt-0.5">
              Parents
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-brand-ink bg-brand-bg"
                    : "text-brand-inkMuted hover:text-brand-ink hover:bg-brand-bg"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/resources"
            data-testid="nav-cta-download"
            className="inline-flex items-center justify-center bg-brand-sage text-white hover:bg-brand-sageDark transition-all px-5 py-2.5 rounded-full text-sm font-medium shadow-soft hover:-translate-y-0.5"
          >
            Free Weekly Reset
          </Link>
        </div>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-toggle"
                aria-label="Open menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-border bg-white text-brand-ink hover:bg-brand-bg transition-colors"
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white border-l border-brand-border">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
              <div className="flex flex-col gap-1 mt-8">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                        isActive
                          ? "text-brand-ink bg-brand-bg"
                          : "text-brand-inkMuted hover:text-brand-ink hover:bg-brand-bg"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/resources"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="mt-4 inline-flex items-center justify-center bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-5 py-3 rounded-full font-medium"
                >
                  Free Weekly Reset
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
